import {
  ColumnDef,
  flexRender,
  Row,
  RowData,
  RowSelectionState,
  Table as TableType,
  useTable,
} from '@tanstack/react-table';
import { appTableFeatures, AppTableFeatures } from './appTableFeatures';
import { useVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { TableRowSkeletonLoader } from '../skeletons/TableRowSkeletonLoader';
import { Down, Up } from './icons';

export const IndeterminateCheckbox = forwardRef<
  HTMLInputElement,
  { indeterminate?: boolean }
>(({ indeterminate, ...rest }, ref) => {
  const defaultRef = useRef<HTMLInputElement>(null);
  const resolvedRef = (ref || defaultRef) as React.RefObject<HTMLInputElement>;

  useEffect(() => {
    if (resolvedRef.current) {
      resolvedRef.current.indeterminate = !!indeterminate;
    }
  }, [resolvedRef, indeterminate]);

  return (
    <>
      <input type='checkbox' ref={resolvedRef} {...rest} />
    </>
  );
});

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

/**
 * Purpose built data table using
 * [*react-table*](https://github.com/tannerlinsley/react-table).
 * Sortable by default. Optionally selectable or clickable rows.
 *
 * To make `Table` rows clickable simply pass a `onRowClick` prop.
 *
 * To make `Table` rows selectable simply pass a `onSelectRow` prop. Note that
 * selectable table rows provide their own click handler which makes this
 * incompatible with `onRowClick`
 *
 * @typedef {object} Props
 * @prop {any} [onRowSelect]
 * @prop {sdiving[]} [initialHiddenColumns]
 * @prop {any} [style]
 *
 * @param {Props} props
 */
export const Table = <TData extends Record<string, any>>({
  columns,
  data,
  containerRef,
  onRowSelect = undefined,
  onRowClick = undefined,
  initialHiddenColumns = [],
  initialSelectedRowIds = undefined,
  initialSortBy,
  activeId = undefined,
  className,
  style,
  isLoading = false,
  ...props
}: {
  columns: ColumnDef<AppTableFeatures, TData, unknown>[];
  data: TData[];
  containerRef: React.RefObject<HTMLDivElement>;
  onRowSelect?: (selectionState: Record<string, boolean>) => void;
  onRowClick?: (row: Row<AppTableFeatures, TData>) => void;
  initialHiddenColumns?: string[];
  initialSelectedRowIds?: Record<string, boolean>;
  initialSortBy?: { id: string; desc: boolean }[];
  activeId?: string | number;
  className?: string;
  style?: React.CSSProperties;
  isLoading?: boolean;
}) => {
  const hoverable = Boolean(onRowSelect) || Boolean(onRowClick);

  const initialColumnVisibility = useMemo(() => {
    return initialHiddenColumns.reduce((acc: Record<string, boolean>, key) => {
      acc[key] = false;
      return acc;
    }, {} as Record<string, boolean>);
  }, [initialHiddenColumns]);

  const initialRowSelection = useMemo((): RowSelectionState => {
    if (!initialSelectedRowIds) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(initialSelectedRowIds)
        .filter(([, selected]) => selected)
        .map(([id]) => [id, true as const])
    );
  }, [initialSelectedRowIds]);

  const initialSorting = useMemo(() => {
    return initialSortBy || [];
  }, [initialSortBy]);

  const idKey = 'id';
  const getRowId = useCallback((row: TData) => row[idKey], [idKey]);

  const defaultData = useMemo(() => [], []);
  const dataToUse = data || defaultData;

  const table = useTable(
    {
      features: appTableFeatures,
      columns,
      data,
      getRowId,
      enableSorting: true,
      enableRowSelection: !!onRowSelect,
      initialState: {
        sorting: initialSorting,
        rowSelection: initialRowSelection,
        columnVisibility: initialColumnVisibility,
      },
      defaultColumn: { size: 150, minSize: 50, maxSize: 500 },
    },
    (state) => state
  );

  const currentSelection = table.state.rowSelection;
  useEffect(() => {
    if (onRowSelect) {
      onRowSelect(currentSelection);
    }
  }, [onRowSelect, currentSelection]);

  return (
    <table
      className={classNames(className)}
      style={{ display: 'grid', ...style }}
      {...props}
    >
      <thead
        className={classNames(
          'sticky top-0 z-10',
          'bg-white dark:bg-gray-800',
          'py-3',
          'pl-1.5 pr-4',
          'border-b border-t border-gray-200 dark:border-gray-700'
        )}
      >
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id} className='flex w-full'>
            {headerGroup.headers.map((header) => (
              <th
                key={header.id}
                colSpan={header.colSpan}
                style={{
                  width: header.getSize(),
                  display: 'flex',
                  alignItems: 'center',
                }}
                className={classNames(
                  'p-2',
                  'text-left text-xs font-semibold text-secondary dark:text-gray-300',
                  header.column.getCanSort()
                    ? 'cursor-pointer select-none'
                    : '',
                  header.column.columnDef.meta?.align === 'right' &&
                    'justify-end'
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div
                  className={classNames(
                    'flex w-full items-center',
                    header.column.columnDef.meta?.align === 'right'
                      ? 'justify-end'
                      : 'justify-start'
                  )}
                >
                  <span
                    className={classNames(
                      'whitespace-nowrap',
                      header.column.columnDef.meta?.align === 'right' &&
                        'text-right'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </span>
                  {(header.column.getIsSorted() ||
                    header.column.columnDef.meta?.align !== 'right') && (
                    <span className='ml-1 inline-block h-4 w-4'>
                      {{
                        asc: <Up />,
                        desc: <Down />,
                      }[header.column.getIsSorted() as string] ?? null}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        ))}
      </thead>

      <TableBody
        table={table}
        containerRef={containerRef}
        activeId={activeId}
        onRowClick={onRowClick}
        hoverable={hoverable}
        isLoading={isLoading}
      />
    </table>
  );
};

Table.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      header: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.node,
      ]),
      cell: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.func,
        PropTypes.node,
      ]),
      accessorKey: PropTypes.string,
      accessorFn: PropTypes.func,
    })
  ),
  data: PropTypes.array,
  onRowClick: PropTypes.func,
  onRowSelect: PropTypes.func,
  initialSelectedRowIds: PropTypes.objectOf(PropTypes.bool),
  initialSortBy: PropTypes.arrayOf(PropTypes.object),
  initialHiddenColumns: PropTypes.arrayOf(PropTypes.string),
  activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isLoading: PropTypes.bool,
};

Table.defaultProps = {
  initialSelectedRowIds: {},
  initialSortBy: [],
  initialHiddenColumns: [],
};

function TableBody<TData extends Record<string, any>>({
  table,
  containerRef,
  activeId,
  onRowClick,
  isLoading = false,
}: {
  table: TableType<AppTableFeatures, TData>;
  containerRef: React.RefObject<HTMLDivElement>;
  activeId?: string | number;
  onRowClick?: (row: Row<AppTableFeatures, TData>) => void;
  hoverable?: boolean;
  isLoading?: boolean;
}) {
  const { rows } = table.getRowModel();
  const rowHeight = 60;
  const numSkeletonRows = 10;
  const hoverable = true;

  const [isContainerMounted, setIsContainerMounted] = React.useState(false);

  React.useEffect(() => {
    if (containerRef.current) {
      setIsContainerMounted(true);
    } else {
      setIsContainerMounted(false);
    }
  }, [containerRef, containerRef.current]);

  const virtualizer = useVirtualizer({
    count: isContainerMounted ? rows.length : 0,
    getScrollElement: () => containerRef.current,
    estimateSize: () => rowHeight,
    overscan: 10,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const skeletonHeight = numSkeletonRows * rowHeight;

  return (
    <tbody
      className={classNames('relative')}
      style={{
        height: isLoading ? `${skeletonHeight}px` : `${totalSize}px`,
        display: 'block',
      }}
    >
      {isLoading
        ? Array.from({ length: numSkeletonRows }).map((_, index) => (
            <TableRowSkeletonLoader
              key={`skeleton-row-${index}`}
              columns={table.getAllLeafColumns()}
              rowHeight={rowHeight}
            />
          ))
        : virtualRows.map((virtualRow: VirtualItem) => {
            const row = rows[virtualRow.index] as Row<AppTableFeatures, TData>;

            if (!row) {
              return null; // Skip rendering this row if data is inconsistent
            }

            const selected =
              row.getIsSelected() || activeId === row.id.toString();
            const added = row.original.queued_type === 'Add';
            const removed = row.original.queued_type === 'Remove';

            return (
              <motion.tr
                key={row.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                onClick={() =>
                  onRowClick ? onRowClick(row) : row.toggleSelected()
                }
                style={{
                  position: 'absolute',
                  transform: `translateY(${virtualRow.start}px)`,
                  width: '100%',
                  display: 'flex',
                  height: `${rowHeight}px`,
                }}
                className={classNames(
                  'py-3',
                  'w-full items-center',
                  'pl-1.5 pr-4',
                  'border-b border-gray-200 dark:border-gray-700',

                  { 'bg-green-50 hover:bg-green-100 dark:bg-green-900': added },
                  { 'bg-red-50 hover:bg-red-100 dark:bg-red-900': removed },
                  {
                    'dark:bg-blue-900 bg-brand-ghost hover:bg-brand-ghost-2':
                      selected,
                  },
                  'hover:bg-gray-50 dark:hover:bg-gray-700'
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{
                      width: cell.column.getSize(),
                      display: 'flex',
                      alignItems: 'center',
                    }}
                    className={classNames(
                      'p-2',
                      'text-xs text-secondary dark:text-gray-200',
                      'whitespace-normal leading-[18px]',
                      cell.column.columnDef.meta?.align === 'right' &&
                        'justify-end'
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </motion.tr>
            );
          })}
    </tbody>
  );
}
